import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

interface SystemSettingsData {
  rate: number;
  telegram_admins: Array<{
    id: number;
    username: string;
    telegram_chat_id?: string;
  }>;
  exempt_ips: string[];
}

const rateSchema = z.object({
  rate: z.number().min(0.01, 'Курс должен быть больше 0'),
});

const ipSchema = z.object({
  ip: z.string().min(7, 'Введите корректный IP адрес'),
});

export function SystemSettings() {
  const [settings, setSettings] = useState<SystemSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const rateForm = useForm<z.infer<typeof rateSchema>>({
    resolver: zodResolver(rateSchema),
    defaultValues: { rate: 100 },
  });

  const ipForm = useForm<z.infer<typeof ipSchema>>({
    resolver: zodResolver(ipSchema),
    defaultValues: { ip: '' },
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/auth/admin/settings', {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.rate !== undefined) {
          // Формат api_auth.py: { rate, telegram_admins, exempt_ips }
          setSettings(data);
          rateForm.reset({ rate: data.rate });
        } else if (data.ok && data.data) {
          // Формат admin.py: { ok: true, data: { rate, telegram_admins, exempt_ips } }
          setSettings(data.data);
          rateForm.reset({ rate: data.data.rate });
        } else {
          console.error('Unexpected response format:', data);
        }
      } else {
        console.error('HTTP Error:', response.status);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRate = async (values: z.infer<typeof rateSchema>) => {
    try {
      setUpdating(true);
      const formData = new FormData();
      formData.append('rate', values.rate.toString());

      const response = await fetch('/api/auth/admin/settings/rate', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        if (data.ok) {
          fetchSettings();
        }
      }
    } catch (error) {
      console.error('Error updating rate:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleAddIP = async (values: z.infer<typeof ipSchema>) => {
    try {
      const formData = new FormData();
      formData.append('ip', values.ip);

      const response = await fetch('/api/auth/admin/exempt_ips', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        if (data.ok) {
          ipForm.reset({ ip: '' });
          fetchSettings();
        }
      }
    } catch (error) {
      console.error('Error adding IP:', error);
    }
  };

  const handleRemoveIP = async (ip: string) => {
    if (!confirm(`Удалить IP ${ip} из исключений?`)) return;

    try {
      const response = await fetch(`/api/auth/admin/exempt_ips/${encodeURIComponent(ip)}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        fetchSettings();
      }
    } catch (error) {
      console.error('Error removing IP:', error);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Настройки системы</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p>Загрузка настроек...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Курс валют */}
      <Card>
        <CardHeader>
          <CardTitle>Курс валют</CardTitle>
          <CardDescription>
            Настройка курса USD к запросам
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={rateForm.handleSubmit(handleUpdateRate)} className="space-y-4">
            <div>
              <Label htmlFor="rate">Курс (1 USD = X запросов)</Label>
              <Input
                id="rate"
                type="number"
                step="0.01"
                {...rateForm.register('rate', { valueAsNumber: true })}
              />
              {rateForm.formState.errors.rate && (
                <p className="text-sm text-red-600 mt-1">
                  {rateForm.formState.errors.rate.message}
                </p>
              )}
            </div>
            <Button type="submit" disabled={updating}>
              {updating ? 'Обновление...' : 'Обновить курс'}
            </Button>
          </form>
          {settings && (
            <div className="mt-4 p-4 bg-muted rounded-lg">
              <p className="text-sm">
                Текущий курс: <strong>1 USD = {settings.rate} запросов</strong>
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* IP исключения */}
      <Card>
        <CardHeader>
          <CardTitle>IP исключения</CardTitle>
          <CardDescription>
            Управление IP адресами для обхода Turnstile
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={ipForm.handleSubmit(handleAddIP)} className="space-y-4 mb-6">
            <div>
              <Label htmlFor="ip">Добавить IP адрес</Label>
              <div className="flex space-x-2">
                <Input
                  id="ip"
                  placeholder="192.168.1.1"
                  {...ipForm.register('ip')}
                />
                <Button type="submit">
                  Добавить
                </Button>
              </div>
              {ipForm.formState.errors.ip && (
                <p className="text-sm text-red-600 mt-1">
                  {ipForm.formState.errors.ip.message}
                </p>
              )}
            </div>
          </form>

          <div className="space-y-2">
            <Label>Текущие исключения:</Label>
            {settings?.exempt_ips.length === 0 ? (
              <p className="text-sm text-muted-foreground">Нет IP исключений</p>
            ) : (
              <div className="space-y-2">
                {settings?.exempt_ips.map((ip) => (
                  <div key={ip} className="flex items-center justify-between p-2 border rounded">
                    <span className="font-mono text-sm">{ip}</span>
                    {ip !== '217.156.122.183' && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleRemoveIP(ip)}
                      >
                        Удалить
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Telegram админы */}
      <Card>
        <CardHeader>
          <CardTitle>Telegram администраторы</CardTitle>
          <CardDescription>
            Список администраторов с настроенным Telegram
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {settings?.telegram_admins.length === 0 ? (
              <p className="text-sm text-muted-foreground">Нет администраторов с Telegram</p>
            ) : (
              <div className="space-y-2">
                {settings?.telegram_admins.map((admin) => (
                  <div key={admin.id} className="flex items-center justify-between p-2 border rounded">
                    <div>
                      <span className="font-medium">{admin.username}</span>
                      {admin.telegram_chat_id && (
                        <span className="text-sm text-muted-foreground ml-2">
                          (ID: {admin.telegram_chat_id})
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
